/**
 * Fase 3.2 (cierre de fuente única) — pieza `AsignarRecibidaModal` / lista `ConciliacionSII`.
 *
 * FUENTE ÚNICA (10.240) de "esta factura sii_recibida está pagada/resuelta", para las dos
 * superficies que hoy usan `estado_vinculacion === 'vinculada'` como proxy de pago: el modal de
 * asignación y el chip/filtro de la lista de ConciliacionSII. Prohibido reimplementar esta
 * composición inline en ninguna pantalla (10.1/10.240) — divergirían en silencio (10.229).
 *
 * NO es lo mismo que `resolverSaldo(...).saldada` (que sigue siendo el criterio PURO del libro,
 * sin tocar — regla 10.224). Medido en `medicion-asignar-recibida-saldada-2026-09-26.md`: migrar
 * 1:1 a `resolverSaldo` rompía 101 facturas ya cerradas que en realidad SÍ están resueltas, pero
 * por un camino que nunca escribe en `aplicaciones_saldo`:
 *   - caja chica (`caja_chica_gastos`) — el gasto se pagó de una caja física, no por banco.
 *   - comisión de factoring vinculada (`operaciones_factoring_comisiones`, regla 10.80) — se
 *     descuenta del giro, nunca genera un pago bancario propio.
 * Por eso el criterio se COMPONE: saldada por el libro, O caja chica, O comisión de factoring.
 *
 * Pendiente, fuera de esta pieza (ver ROADMAP_TECNICO): cuando caja chica escriba su propio
 * evento en el libro (como los demás caminos de pago), esta composición se puede retirar y volver
 * a `resolverSaldo` puro — la fuente vuelve a ser realmente una.
 */

import { resolverSaldo } from "../cobro/resolverSaldo";
import { client } from "../supabase";
import { fetchCtxLibroPago } from "./saldosPorFacturaProveedor";

/**
 * @param {{id:number, monto_total:number}} factura
 * @param {{ctxLibro:object, cajaChicaFacturaIds:Set<number>, comisionFactoringIds:Set<number>}} ctx
 *   resultado de `construirContextoFacturaRecibidaPagada` (o un ctx sintético para tests).
 * @returns {boolean}
 */
export const facturaRecibidaEstaPagada = (factura, ctx) => {
  if (!factura || factura.id == null) return false;
  const { ctxLibro, cajaChicaFacturaIds, comisionFactoringIds } = ctx || {};
  if (cajaChicaFacturaIds?.has(factura.id)) return true;
  if (comisionFactoringIds?.has(factura.id)) return true;
  return resolverSaldo({ tipo: "sii_recibida", id: factura.id, monto_total: factura.monto_total }, ctxLibro).saldada;
};

/**
 * Arma el contexto compartido con una sola tanda de queries — ambos consumidores (modal, lista)
 * llaman esto igual, sin repetir queries a mano (10.1). `caja_chica_gastos` sí es país-scoped
 * (columna propia); `operaciones_factoring_comisiones` y el libro no lo son (mismo criterio que
 * ya usan `FlujoCajaV2`/`getSaldosPorFacturaProveedor` — comparar por `factura.id`, nunca por
 * nombre, hace que el filtro de país sea innecesario acá: 10.7).
 *
 * @param {string} pais
 */
export const construirContextoFacturaRecibidaPagada = async (pais) => {
  const [ccRes, ofcRes, ctxLibro] = await Promise.all([
    client.from("caja_chica_gastos").select("factura_id").eq("factura_tipo", "sii_recibida").eq("pais", pais),
    client.from("operaciones_factoring_comisiones").select("factura_sii_recibida_id"),
    fetchCtxLibroPago(),
  ]);
  if (ccRes.error) throw ccRes.error;
  if (ofcRes.error) throw ofcRes.error;
  return {
    ctxLibro,
    cajaChicaFacturaIds: new Set((ccRes.data || []).map((r) => r.factura_id)),
    comisionFactoringIds: new Set((ofcRes.data || []).map((r) => r.factura_sii_recibida_id)),
  };
};
